/**
 * An AI call campaign's queue: how many companies wait, the button that fills
 * it, and the companies themselves in the order they would be rung.
 *
 * "Fill the queue" tops the queue up to two days of the campaign's cap from
 * the audience as it is today; the answer says how many were added (an alert
 * campaign's urgent defects may pass a full queue), why there was no room when
 * nothing was, and the client defect tasks the same press filed
 * (`lib/aiCampaignPage.ts`). Nothing on this card rings anybody:
 * the automation (ring R4) works the queue when it is switched on, and the card
 * says when that is. Each row says what the automation has done with the
 * company so far: how many calls, when the next try is due, why it left the
 * queue, and the last call's result (`targetRowFacts`). A company in the queue
 * can be tried out with a test call to the manager's own phone, with this
 * campaign's words and call flow map (ring R8).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListPlus, Loader2, PhoneOutgoing } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  QUEUE_DAYS,
  TARGET_STATUSES,
  enrolAiCampaign,
  listAiCampaignTargets,
  type AiCallCampaign,
  type AiCallTarget,
  type EnrolOutcome,
} from "../../../../lib/aiCampaigns";
import { targetRowFacts } from "../../../../lib/aiCampaignAutomation";
import { enrolNote } from "../../../../lib/aiCampaignPage";
import { dispositionKey, dispositionTone } from "../../../../lib/aiOutboundResults";
import { formatPhone } from "../../../../lib/phone";
import { useAiTestCall } from "../../../telephony/aiTestCall";
import { Btn, IconBtn, StatusPill, fmtDateTime, fmtNum } from "../../../ui";
import { Panel } from "../../marketing/shared";
import { Note } from "../../settings/panel/fields";

export function CampaignQueueCard({
  campaign,
  canManage,
  onFilled,
  reloadKey = 0,
}: {
  campaign: AiCallCampaign;
  canManage: boolean;
  /** The queue counts changed: the page reloads the campaign. */
  onFilled: () => void;
  /** Bumped by the page when something outside this card changed the rows (a call placed now). */
  reloadKey?: number;
}) {
  const t = useT();
  const testCall = useAiTestCall();
  const [rows, setRows] = useState<AiCallTarget[] | null>(null);
  const [nextAfter, setNextAfter] = useState<string | null>(null);
  const [filling, setFilling] = useState(false);
  const [outcome, setOutcome] = useState<EnrolOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const page = await listAiCampaignTargets(campaign.id);
      setRows(page.rows);
      setNextAfter(page.nextAfter);
    } catch {
      setRows([]);
      setNextAfter(null);
    }
  }, [campaign.id]);

  // `reloadKey` is only a trigger: a new value reads the rows again.
  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  async function more() {
    if (!nextAfter) return;
    const page = await listAiCampaignTargets(campaign.id, nextAfter).catch(() => null);
    if (!page) return;
    setRows((prev) => [...(prev ?? []), ...page.rows]);
    setNextAfter(page.nextAfter);
  }

  async function fill() {
    setFilling(true);
    setError(null);
    try {
      const result = await enrolAiCampaign(campaign.id);
      setOutcome(result);
      await load();
      onFilled();
    } catch (err) {
      const code = err && typeof err === "object" && "message" in err ? String((err as Error).message) : "";
      setError(code === "campaign_done" || code === "no_audience" ? code : "failed");
    } finally {
      setFilling(false);
    }
  }

  const waiting = campaign.targets.pending + campaign.targets.retry;
  const note = outcome ? enrolNote(outcome) : null;

  return (
    <Panel
      title={t("aiCalls.campaigns.queue.title")}
      description={t("aiCalls.campaigns.queue.intro", { days: QUEUE_DAYS, max: fmtNum(campaign.dailyCap * QUEUE_DAYS) })}
      actions={
        canManage ? (
          <Btn onClick={() => void fill()} disabled={filling || campaign.status === "done"}>
            {filling ? <Loader2 className="size-3.5 animate-spin" /> : <ListPlus className="size-3.5" />}
            {t("aiCalls.campaigns.queue.fill")}
          </Btn>
        ) : null
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {TARGET_STATUSES.map((status) => (
            <StatusPill key={status} tone={status === "pending" || status === "retry" ? "info" : "mut"}>
              {t(`aiCalls.campaigns.targetStatus.${status}`)} {fmtNum(campaign.targets[status])}
            </StatusPill>
          ))}
        </div>

        {outcome && note ? (
          <Note tone={note.tone} live>
            {note.kind === "full"
              ? t("aiCalls.campaigns.queue.full", { waiting: fmtNum(outcome.waiting) })
              : t("aiCalls.campaigns.queue.added", { added: fmtNum(outcome.added), callable: fmtNum(outcome.callable) })}
            {note.clientTasks > 0 ? <> {t("aiCalls.campaigns.queue.clientTasks", { n: note.clientTasks })}</> : null}
          </Note>
        ) : null}
        {error ? <Note tone="bad" live>{t(`aiCalls.campaigns.queue.error.${error}`)}</Note> : null}

        <Note tone="info">{t("aiCalls.campaigns.queue.noCallsYet")}</Note>

        {rows === null ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="crm-sub text-sm">{t(waiting === 0 ? "aiCalls.campaigns.queue.empty" : "aiCalls.campaigns.queue.loadFailed")}</p>
        ) : (
          <>
            <ol className="divide-y divide-border text-sm">
              {rows.map((row, index) => {
                const facts = targetRowFacts(row);
                return (
                  <li key={row.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-2">
                    <span className="crm-sub w-6 shrink-0 text-xs tabular-nums">{index + 1}.</span>
                    <span className="min-w-0 flex-1 truncate">
                      {row.regCode ? (
                        <Link to={`/ariregister/${row.regCode}`} className="hover:underline">
                          {row.companyName || row.domain || row.regCode}
                        </Link>
                      ) : (
                        row.companyName || row.domain
                      )}
                    </span>
                    <span className="crm-sub text-xs tabular-nums">{formatPhone(row.phone)}</span>
                    <StatusPill tone={row.status === "pending" || row.status === "retry" ? "info" : "mut"}>
                      {t(`aiCalls.campaigns.targetStatus.${(TARGET_STATUSES as readonly string[]).includes(row.status) ? row.status : "pending"}`)}
                    </StatusPill>
                    {facts.disposition !== null ? (
                      <StatusPill tone={dispositionTone(facts.disposition)}>{t(`aiCalls.disposition.${dispositionKey(facts.disposition)}`)}</StatusPill>
                    ) : null}
                    {facts.skipKey !== null ? (
                      <span className="crm-sub text-xs">{t(`aiCalls.campaigns.targetSkip.${facts.skipKey}`)}</span>
                    ) : null}
                    {facts.attempts !== null ? (
                      <span className="crm-sub text-xs tabular-nums">{t("aiCalls.campaigns.queue.attempts", { n: facts.attempts })}</span>
                    ) : null}
                    {facts.nextAttemptAt !== null ? (
                      <span className="crm-sub text-xs tabular-nums">
                        {t("aiCalls.campaigns.queue.nextAttempt", { at: fmtDateTime(facts.nextAttemptAt) })}
                      </span>
                    ) : null}
                    {testCall.canCall ? (
                      <IconBtn
                        small
                        aria-label={t("aiCalls.campaigns.queue.testCall")}
                        title={t("aiCalls.campaigns.queue.testCall")}
                        onClick={() =>
                          testCall.open({
                            regCode: row.regCode,
                            domain: row.domain,
                            name: row.companyName,
                            campaignId: campaign.id,
                          })
                        }
                      >
                        <PhoneOutgoing className="size-3.5" />
                      </IconBtn>
                    ) : null}
                  </li>
                );
              })}
            </ol>
            {nextAfter ? (
              <div className="flex justify-center">
                <Btn onClick={() => void more()}>{t("aiCalls.more")}</Btn>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Panel>
  );
}
